import { AppointmentTimeSlotPlan } from "@rumble-it/common-lib/types/models/AppointmentTimeSlotPlan.interface";
import { UseMutationResult, useQuery } from "@tanstack/react-query";
import { Card } from "@rumbledirect/ui";
import { Table } from "@/components/v2";
import { Col, Flex, Form, Row } from "antd";
import AppointmentTimeSlotPlanItemsEditor from "./AppointmentTimeSlotPlanItemsEditor";
import { fetchServicepartnersWithoutPagination } from "../../../../../lib/api/resources/servicePartners";
import { Company } from "@rumble-it/common-lib/types/models/Company.interface";
import { fetchCompanies } from "../../../../../lib/api/resources/companies";
import { fetchDossierTypesWithoutPagination } from "../../../../../lib/api/resources/dossierTypes";
import { DossierType } from "@rumble-it/common-lib/types/models/DossierType.interface";
import { AppointmentTimeSlotPlanItem } from "@rumble-it/common-lib/types/models/AppointmentTimeSlotPlanItem.interface";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { Button, Input, InputNumber, Select } from "@rumbledirect/ui";
import { TableColumnsType, TableProps } from "antd";

interface ServicePartnerRow {
  key: string;
  id: string;
  groupName: string | null;
  name: string;
  city: string | null;
}

export type AppointmentTimeSlotPlanItemFormValues = Omit<
  AppointmentTimeSlotPlanItem,
  "id" | "@id" | "startTime" | "endTime"
> & {
  id?: string;
  startTime: Dayjs | string;
  endTime: Dayjs | string;
  _key?: string;
};

export type AppointmentTimeSlotPlanFormValues = Omit<
  AppointmentTimeSlotPlan,
  | "servicePartners"
  | "company"
  | "dealership"
  | "holidayConfig"
  | "planItems"
  | "dossierType"
> & {
  servicePartners: string[];
  company: string | null;
  dealership: string | null;
  holidayConfig: string | null;
  dossierType: string;
  planItems: AppointmentTimeSlotPlanItemFormValues[];
};

interface AppointmentTimeSlotPlanFormCardProps {
  mutation: UseMutationResult<
    AppointmentTimeSlotPlan | null,
    Error,
    AppointmentTimeSlotPlanFormValues,
    unknown
  >;
  initialValues?: AppointmentTimeSlotPlanFormValues;
  loading?: boolean;
  title?: string;
  buttonText?: string;
}

export const AppointmentTimeSlotPlanFormCard = ({
  mutation,
  initialValues,
  title = "Planning aanmaken",
  buttonText = "Aanmaken",
  loading = false,
}: AppointmentTimeSlotPlanFormCardProps) => {
  const [form] = Form.useForm();
  const [servicePartnerSearchValue, setServicePartnerSearchValue] =
    useState("");

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  const servicePartnersQuery = useQuery({
    queryKey: ["servicePartners", "table"],
    queryFn: async () => {
      const response = await fetchServicepartnersWithoutPagination();
      return response.map((servicePartner) => ({
        key: servicePartner.id,
        id: servicePartner.id,
        groupName: servicePartner.groupName ?? null,
        name: servicePartner.name,
        city: servicePartner.city ?? null,
      }));
    },
  });

  const companiesQuery = useQuery({
    queryKey: ["companies", "no-pagination"],
    queryFn: async () => {
      const companies = await fetchCompanies({
        pagination: false,
        type: ["lease", "insurance"],
      });
      return companies.member.map((company: Company) => ({
        label: company.fullName,
        value: company["id"] as string,
      }));
    },
  });

  const dealershipQuery = useQuery({
    queryKey: ["dealership", "no-pagination"],
    queryFn: async () => {
      const dealership = await fetchCompanies({
        pagination: false,
        type: ["dealership"],
      });
      return dealership.member.map((dealership: Company) => ({
        label: dealership.fullName,
        value: dealership["id"] as string,
      }));
    },
  });

  const dossierTypeQuery = useQuery({
    queryKey: ["dossierTypes", "no-pagination"],
    queryFn: async () => {
      const dossierTypes = await fetchDossierTypesWithoutPagination();
      return (
        dossierTypes.member.find(
          (dossierType: DossierType) =>
            dossierType.name === "repair_direct.generic",
        ) || null
      );
    },
  });

  const handleFinish = (values: AppointmentTimeSlotPlanFormValues) => {
    const planItems = (values.planItems || []).map(
      ({ _key, ...planItem }) => planItem,
    );

    const payload = {
      ...values,
      servicePartners: values.servicePartners ?? [],
      dossierType: dossierTypeQuery.data?.id || "",
      planItems,
    };
    mutation.mutate(payload);
  };

  const selectedServicePartnerIds =
    Form.useWatch("servicePartners", form) ?? [];

  const filteredServicePartners = (servicePartnersQuery.data ?? []).filter(
    (servicePartner) => {
      const normalizedSearch = servicePartnerSearchValue.trim().toLowerCase();
      if (normalizedSearch.length === 0) {
        return true;
      }

      return (
        (servicePartner.groupName ?? "")
          .toLowerCase()
          .includes(normalizedSearch) ||
        servicePartner.name.toLowerCase().includes(normalizedSearch) ||
        (servicePartner.city ?? "").toLowerCase().includes(normalizedSearch)
      );
    },
  );

  const servicePartnerColumns: TableColumnsType<ServicePartnerRow> = [
    {
      title: "Groep Naam",
      dataIndex: "groupName",
      key: "groupName",
      render: (groupName: string | null) => groupName || "-",
    },
    {
      title: "Naam",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Plaats",
      dataIndex: "city",
      key: "city",
      render: (city: string | null) => city || "-",
    },
  ];

  const servicePartnerRowSelection: TableProps<ServicePartnerRow>["rowSelection"] =
    {
      selectedRowKeys: selectedServicePartnerIds,
      onChange: (selectedRowKeys) => {
        const visibleServicePartnerIds = new Set(
          filteredServicePartners.map((servicePartner) => servicePartner.id),
        );
        const hiddenSelectedIds = (
          selectedServicePartnerIds as string[]
        ).filter((id) => !visibleServicePartnerIds.has(id));

        form.setFieldValue("servicePartners", [
          ...hiddenSelectedIds,
          ...(selectedRowKeys as string[]),
        ]);
      },
    };

  const handleSelectAllFilteredServicePartners = () => {
    const filteredIds = filteredServicePartners.map(
      (servicePartner) => servicePartner.id,
    );
    const mergedIds = Array.from(
      new Set([...(selectedServicePartnerIds as string[]), ...filteredIds]),
    );
    form.setFieldValue("servicePartners", mergedIds);
  };

  const handleClearServicePartnersSelection = () => {
    form.setFieldValue("servicePartners", []);
  };

  return (
    <Form form={form} onFinish={handleFinish} layout="vertical">
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Flex vertical gap={16}>
            <Card loading={loading} title={title}>
              <Form.Item
                rules={[{ required: true, message: "Dit veld is verplicht" }]}
                label="Naam"
                name="name"
              >
                <Input />
              </Form.Item>
              <Form.Item
                rules={[
                  {
                    max: 160,
                    message:
                      "De inhoud van de beschrijving mag maximaal 160 karakters bevatten.",
                  },
                ]}
                label="Beschrijving"
                name="description"
              >
                <Input.TextArea />
              </Form.Item>
              <Form.Item name="company" label="Opdrachtgever">
                <Select
                  placeholder="Selecteer een opdrachtgever"
                  options={companiesQuery.data}
                  loading={companiesQuery.isFetching}
                  showSearch
                  optionFilterProp="label"
                  notFoundContent="Geen opdrachtgevers gevonden"
                />
              </Form.Item>
              <Form.Item name="dealership" label="Dealer">
                <Select
                  placeholder="Selecteer een dealer"
                  options={dealershipQuery.data}
                  loading={dealershipQuery.isFetching}
                  showSearch
                  optionFilterProp="label"
                  notFoundContent="Geen dealers gevonden"
                />
              </Form.Item>
              <Form.Item
                label="Aantal werkdagen voor start planning"
                name="minimumStartWorkingDays"
                rules={[{ required: true, message: "Dit veld is verplicht" }]}
              >
                <InputNumber
                  className="w-full"
                  min={0}
                  step={1}
                  precision={0}
                />
              </Form.Item>
              <Form.Item
                label="Aantal maanden voor einde planning"
                name="maximumEndMonths"
                rules={[{ required: true, message: "Dit veld is verplicht" }]}
              >
                <InputNumber
                  className="w-full"
                  min={0}
                  step={1}
                  precision={0}
                />
              </Form.Item>
              <Form.Item
                label="Feestdagen configuratie"
                name="holidayConfig"
                rules={[{ required: true, message: "Dit veld is verplicht" }]}
              >
                <Select
                  options={[
                    {
                      value: "nl-national",
                      label: "Nederlandse nationale feestdagen",
                    },
                  ]}
                />
              </Form.Item>
              <Form.Item label={null}>
                <Button
                  loading={mutation.isPending}
                  type="primary"
                  htmlType="submit"
                >
                  {buttonText}
                </Button>
              </Form.Item>
            </Card>
          </Flex>
        </Col>
        <Col span={12}>
          <Flex vertical gap={16}>
            <Form.Item
              name="planItems"
              label={null}
              rules={[
                { required: true, message: "Voeg minimaal één tijdslot toe" },
              ]}
            >
              <AppointmentTimeSlotPlanItemsEditor loading={loading} />
            </Form.Item>
            <Card loading={servicePartnersQuery.isFetching} title="Herstellers">
              <Flex vertical gap={12}>
                <Input
                  value={servicePartnerSearchValue}
                  onChange={(event) =>
                    setServicePartnerSearchValue(event.target.value)
                  }
                  placeholder="Zoek op groep naam, naam of plaats"
                />
                <Flex gap={8}>
                  <Button
                    variant="light"
                    onClick={handleSelectAllFilteredServicePartners}
                  >
                    Selecteer gefilterde
                  </Button>
                  <Button
                    variant="light"
                    onClick={handleClearServicePartnersSelection}
                  >
                    Wis selectie
                  </Button>
                </Flex>
                <Form.Item name="servicePartners" hidden>
                  <Input />
                </Form.Item>
                <Table
                  rowSelection={servicePartnerRowSelection}
                  columns={servicePartnerColumns}
                  dataSource={filteredServicePartners}
                  locale={{ emptyText: "Geen herstellers gevonden" }}
                  size="small"
                  pagination={false}
                  onRow={(servicePartner) => ({
                    onClick: () => {
                      const selectedIds = selectedServicePartnerIds as string[];
                      const isSelected = selectedIds.includes(
                        servicePartner.id,
                      );
                      form.setFieldValue(
                        "servicePartners",
                        isSelected
                          ? selectedIds.filter((id) => id !== servicePartner.id)
                          : [...selectedIds, servicePartner.id],
                      );
                    },
                  })}
                />
              </Flex>
            </Card>
          </Flex>
        </Col>
      </Row>
    </Form>
  );
};
